import { useState } from 'react';
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import {
  CustomField,
  CustomUserProfileFieldEnum,
  UserPermissionEnum,
} from 'src/models/GalenData/CustomField.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { UserStatusEnum } from 'src/models/GalenData/User.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';
import useValidatePassword from 'src/hooks/useValidatePassword';

import { emailPattern, namePattern } from 'src/utils/validationRules';

export type AddUserFormDataTypes = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
};

interface IAddUserFormModalProps {
  show: boolean;
  handleClose: () => void;
  initializeUsersList: () => void;
}

const AddUserFormModal = ({
  show,
  handleClose,
  initializeUsersList,
}: IAddUserFormModalProps) => {
  const {
    handleSubmit,
    reset,
    register,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const handleCloseAndReset = () => {
    handleClose();
    reset();
  };

  const { tCommon, tPages, language } = useUiTranslation();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const validatePassword = useValidatePassword();

  const { practiceId } = useGetCurrentUser();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { getErrorMessage } = useGetGalenErrorMsg();

  const {
    practiceSettings: { hasMfa },
  } = useRemoteBoundStore();

  const handleAddUser = async (data: AddUserFormDataTypes) => {
    try {
      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      const { firstName, lastName, emailAddress, password } = data;

      const res = await Api.remote.getUserCustomFieldDefinitions();

      const customData = [
        {
          field: res.data.content?.find(
            (definition) =>
              definition.name === CustomUserProfileFieldEnum.UserPermission,
          ) as CustomField<CustomUserProfileFieldEnum>,
          fieldData: [UserPermissionEnum.Create],
        },
      ];

      const userId = uuidv4();

      await Api.remote.create_user({
        user: {
          userId,
          firstName,
          lastName,
          emailAddress,
          tenant: { tenantId: import.meta.env.VITE_TENANT_ID },
          roles: [
            {
              defaultRole: true,
              practice: { practiceId },
              role: UserRoleEnum.PracticeUser,
            },
          ],
          status: UserStatusEnum.Active,
          loggableAccount: true,
        },
        password,
        forcePasswordReset: true,
        customData,
      });

      if (hasMfa) {
        await Api.remote.set_user_mfa(true, userId);
      }

      initializeUsersList();
      Api.alertBox({
        title: tCommon('bootbox.title.Success'),
        message: tPages('users.addUserSuccessMessage', {
          emailAddress,
          fullName: `${firstName} ${lastName}`,
        }),
        locale: getLocaleCode(language as Language),
        type: 'success',
      });

      handleCloseAndReset();
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  return (
    <CustomModal show={show} onHide={handleCloseAndReset}>
      <Form onSubmit={handleSubmit(handleAddUser)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {tPages('users.add')} {tPages('users.user')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{tCommon('patientFields.First Name')}</Form.Label>

            <Form.Control
              type="text"
              placeholder={tCommon('patientFields.firstNamePlaceholder')}
              data-test="first-name-input"
              {...register('firstName', {
                required: tCommon('patientFields.firstNameRequired'),
                maxLength: {
                  value: 100,
                  message: tCommon('patientFields.firstNameMaxLength'),
                },
                pattern: {
                  value: namePattern,
                  message: tCommon('patientFields.firstNameInvalid'),
                },
              })}
            />
            {errors.firstName?.message && (
              <p className="text-danger">{errors.firstName?.message}</p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{tCommon('patientFields.Last Name')}</Form.Label>
            <Form.Control
              placeholder={tCommon('patientFields.lastNamePlaceholder')}
              data-test="last-name-input"
              {...register('lastName', {
                required: tCommon('patientFields.lastNameRequired'),
                maxLength: {
                  value: 100,
                  message: tCommon('patientFields.lastNameMaxLength'),
                },
                pattern: {
                  value: namePattern,
                  message: tCommon('patientFields.lastNameInvalid'),
                },
              })}
            />
            {errors.lastName?.message && (
              <p className="text-danger ">{errors.lastName?.message}</p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{tCommon('patientFields.email')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={tCommon('patientFields.emailPlaceholder')}
              data-test="email-input"
              autoComplete="off"
              {...register('emailAddress', {
                required: tCommon('patientFields.emailRequired'),
                pattern: {
                  value: emailPattern,
                  message: tCommon('patientFields.emailInvalid'),
                },
                maxLength: {
                  value: 254,
                  message: tCommon('patientFields.emailMaxLength'),
                },
              })}
            ></Form.Control>
            {errors.emailAddress?.message && (
              <p className="text-danger ">{errors.emailAddress?.message}</p>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{tCommon('patientFields.password')}</Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowPassword ? 'text' : 'password'}
                placeholder={tCommon('patientFields.passwordPlaceholder')}
                data-test="password-input"
                {...register('password', {
                  required: tCommon('patientFields.passwordRequired'),
                  validate: validatePassword,
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowPassword(!isShowPassword)}
                className="pointer"
              >
                <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
              </InputGroup.Text>
            </InputGroup>
            <div className="text-danger">
              {errors.password?.type === 'required' ? (
                <p>{tCommon('patientFields.passwordRequired')}</p>
              ) : (
                <ul className="list-unstyled">
                  {errors.password?.types?.minLength && (
                    <li>{errors.password.message}</li>
                  )}
                  {errors.password?.types?.hasUpperCase && (
                    <li>{errors.password.types.hasUpperCase}</li>
                  )}
                  {errors.password?.types?.hasLowerCase && (
                    <li>{errors.password.types.hasLowerCase}</li>
                  )}
                  {errors.password?.types?.hasNumber && (
                    <li>{errors.password.types.hasNumber}</li>
                  )}
                  {errors.password?.types?.hasSpecialChar && (
                    <li>{errors.password.types.hasSpecialChar}</li>
                  )}
                </ul>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{tCommon('patientFields.confirmPassword')}</Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowConfirmPassword ? 'text' : 'password'}
                placeholder={tCommon(
                  'patientFields.confirmPasswordPlaceholder',
                )}
                data-test="confirm-password-input"
                {...register('confirmPassword', {
                  required: tCommon('patientFields.confirmPasswordRequired'),
                  validate: (val: string) => {
                    if (watch('password') !== val) {
                      return tCommon('patientFields.passwordsDoNotMatch');
                    }
                  },
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                className="pointer"
              >
                <FontAwesomeIcon
                  icon={isShowConfirmPassword ? faEye : faEyeSlash}
                />
              </InputGroup.Text>
            </InputGroup>
            {<p className="text-danger ">{errors.confirmPassword?.message}</p>}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="primary-100" onClick={handleCloseAndReset}>
            {tPages('users.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            data-test="submit-member-form"
            disabled={isSubmitting || !isDirty}
          >
            {isSubmitting ? tPages('users.saving') : tPages('users.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </CustomModal>
  );
};

export default AddUserFormModal;
